import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { ProductDTO } from "../../api/product";
import { DescriptionsTab } from "./DescriptionsTab";
import { GeneralTab } from "./GeneralTab";
import { AssemblyTab, CertificationsTab, SupplementaryTab } from "./specTabs";

/**
 * Everything typed about a product, on one scrolling tab: the general fields,
 * the EN/FR texts, and — for items, not bundles — assembly, supplementary and
 * certification data. These were five tabs; the forms are unchanged and the
 * page's Save still covers them all. Each section keeps its own heading so the
 * eye can jump, and its own anchor so a link can land on it.
 */
export function DataTab({
  draft,
  onChange,
  readOnly,
  onPersisted,
}: {
  draft: ProductDTO;
  onChange: (next: ProductDTO) => void;
  readOnly: boolean;
  onPersisted: () => void;
}) {
  const { t } = useTranslation();
  const item = draft.type !== "BUNDLE";
  return (
    <div className="flex flex-col gap-8">
      <Section id="general" title={t("product.tab.general", "General")}>
        <GeneralTab draft={draft} onChange={onChange} readOnly={readOnly} />
      </Section>
      <Section id="descriptions" title={t("product.tab.descriptions", "Descriptions")}>
        <DescriptionsTab draft={draft} onChange={onChange} readOnly={readOnly} />
      </Section>
      {item && (
        <Section id="assembly" title={t("product.tab.assembly", "Assembly")}>
          <AssemblyTab draft={draft} onChange={onChange} readOnly={readOnly} onPersisted={onPersisted} />
        </Section>
      )}
      {item && (
        <Section id="supplementary" title={t("product.tab.supplementary", "Supplementary")}>
          <SupplementaryTab draft={draft} onChange={onChange} readOnly={readOnly} />
        </Section>
      )}
      {item && (
        <Section id="certifications" title={t("product.tab.certifications", "Certifications")}>
          <CertificationsTab draft={draft} onChange={onChange} readOnly={readOnly} />
        </Section>
      )}
    </div>
  );
}

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} className="scroll-mt-4">
      <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-ink-faint">{title}</h2>
      {children}
    </section>
  );
}
